import React, { useState, useEffect } from 'react';
import { Icon } from '../Icon';
import { formatCurrency, formatDateWithTime, formatDate, stripEmojis, localizeCategory } from '../../utils/format';
import { getCategoryIconConfig } from '../../utils/categoryIcons';
import { getAllCategoryIcons, categoryIconsEventEmitter, CATEGORY_ICONS_CHANGED_EVENT } from '../../utils/categoryIconStorage';
import './TransactionCard.css';

interface Transaction {
  id: string | number;
  type: 'income' | 'expense';
  title: string;
  amount: number;
  category?: string;
  date: string;
  is_recurring?: boolean;
  recurring_type?: 'daily' | 'weekly' | 'monthly' | 'yearly';
  source?: 'plaid' | 'manual';
  plaid_account?: string;
  plaid_mask?: string;
  plaid_institution?: string;
  plaid_transaction_id?: string;
  is_internal_transfer?: boolean;
}

interface TransactionCardProps {
  transaction: Transaction;
  currency: string;
  isLast?: boolean;
  onClick: (transaction: Transaction) => void;
  onDelete?: (transaction: Transaction) => void;
  isDeleting?: boolean;
}

export const TransactionCard: React.FC<TransactionCardProps> = ({
  transaction,
  currency,
  isLast = false,
  onClick,
  onDelete,
  isDeleting = false,
}) => {
  const [categoryIcons, setCategoryIcons] = useState<Record<string, string>>({});

  useEffect(() => {
    const loadCategoryIcons = () => {
      try {
        const icons = getAllCategoryIcons();
        setCategoryIcons(icons);
      } catch (error) {
        console.error('Failed to load category icons:', error);
        setCategoryIcons({});
      }
    };

    loadCategoryIcons();

    const handleIconsChanged = (newIcons: Record<string, string>) => {
      setCategoryIcons(newIcons);
    };

    categoryIconsEventEmitter.on(CATEGORY_ICONS_CHANGED_EVENT, handleIconsChanged);

    return () => {
      categoryIconsEventEmitter.off(CATEGORY_ICONS_CHANGED_EVENT, handleIconsChanged);
    };
  }, []);

  const isRecurring = transaction.is_recurring || 
                     String(transaction.description || '').toLowerCase().includes('регулярн');
  const isGoalDeposit = transaction.category === 'Цели' || 
                        String(transaction.description || '').toLowerCase().includes('пополнение цели');

  // Определяем иконку для транзакции
  const renderIcon = () => {
    if (transaction.type === 'income') {
      // Для доходов используем иконку доллара с зеленым цветом
      return (
        <div 
          className="transaction-item__icon"
          style={{
            backgroundColor: 'var(--neo-bg-light)',
          }}
        >
          <Icon 
            name="dollar-sign" 
            size={20} 
            color="#34C759"
          />
        </div>
      );
    }

    if (transaction.category) {
      // Для расходов используем иконку категории
      const customIconName = categoryIcons[transaction.category] || null;
      const iconConfig = getCategoryIconConfig(transaction.category, customIconName);
      
      return (
        <div 
          className="transaction-item__icon"
          style={{
            backgroundColor: iconConfig.backgroundColor,
          }}
        >
          <Icon 
            name={iconConfig.icon} 
            size={20} 
            color={iconConfig.color}
          />
        </div>
      );
    }

    // Дефолтная иконка для расходов без категории
    return (
      <div 
        className="transaction-item__icon"
        style={{
          backgroundColor: '#FF3B30',
        }}
      >
        <Icon 
          name="arrow-down" 
          size={16} 
          color="white"
        />
      </div>
    );
  };

  return (
    <div 
      className={`transaction-item ${isLast ? 'transaction-item--last' : ''}`}
      onClick={() => onClick(transaction)}
      style={{ cursor: 'pointer' }}
    >
      <div className="transaction-item__left">
        {renderIcon()}
        <div className="transaction-item__info">
          <div className="transaction-item__title">{transaction.title}</div>
          <div className="transaction-item__meta">
            {transaction.category && (
              <span className="transaction-item__category">{localizeCategory(stripEmojis(transaction.category))}</span>
            )}
            <span className="transaction-item__date">
              {transaction.source === 'plaid' 
                ? formatDate(transaction.date)
                : formatDateWithTime(transaction.date)}
            </span>
            {/* Регулярный маркер */}
            {isRecurring && (
              <span className="transaction-item__recurring" title="Регулярная транзакция">
                <Icon name="repeat" size={12} />
              </span>
            )}
            {/* Маркер цели */}
            {isGoalDeposit && (
              <span 
                className="transaction-item__badge transaction-item__badge--goal" 
                title="Пополнение цели"
              >
                ЦЕЛЬ
              </span>
            )}
            {/* Информация о Plaid счете */}
            {transaction.source === 'plaid' && (transaction.plaid_account || transaction.plaid_mask) && (
              <span className="transaction-item__plaid-account" title={`${transaction.plaid_institution || ''} ${transaction.plaid_account || ''}`}>
                {transaction.plaid_mask ? `•••• ${transaction.plaid_mask}` : transaction.plaid_account || ''}
              </span>
            )}
            {/* Индикатор внутреннего перевода */}
            {transaction.is_internal_transfer && (
              <span className="transaction-item__internal-transfer" title="Внутренний перевод между счетами">
                <Icon name="swap-horizontal" size={12} />
              </span>
            )}
          </div>
        </div>
      </div>
      <div className="transaction-item__right">
        <div 
          className={`transaction-item__amount ${
            transaction.type === 'income' 
              ? 'transaction-item__amount--income' 
              : 'transaction-item__amount--expense'
          }`}
        >
          {formatCurrency(transaction.amount, currency)}
        </div>
        {onDelete && transaction.source !== 'plaid' && (
          <button
            type="button"
            className="transaction-item__delete"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(transaction);
            }}
            disabled={isDeleting}
            aria-label="Удалить"
            title="Удалить"
          >
            <Icon name="trash" size={18} color="var(--color-danger)" />
          </button>
        )}
      </div>
    </div>
  );
};

